import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import {
  Button, Checkbox, Form, Header, Divider,
} from 'semantic-ui-react';

import {
  sendSetPassword,
  clearError as accountClearError,
  verifyVerificationToken,
} from '../modules/account';
import {
  setCredentialData,
  sendSignInRequest,
  clearError as sessionClearError,
} from '../modules/session';

import CenterCard from './center_card';

import Loading from './loader_wave';
import ErrorMessage from './error_message';
import EmailNewSetPassword from './email_set_new_password';

import ErrorType from './types/error_type';

// This is enforced by the API, but the policy isn't published.  See mrp-api issue #94.
const PASSWORD_MIN_LENGTH = 8;
const CHECK_PASSWORD_DEBOUNCE_MS = 750;

class Verify extends Component {
  constructor() {
    super();

    this.state = {
      acceptTerms: false,
      password: '',
      passwordLengthError: false,
      submitted: false,
    };
    this.debouncedCheckPasswordLength = debounce(this.checkPasswordLength.bind(this),
      CHECK_PASSWORD_DEBOUNCE_MS);
  }

  componentDidMount() {
    const {
      computedMatch,
      connectedAccountClearError,
      connectedSessionClearError,
      connectedSetCredentialData,
      connectedVerifyVerificationToken,
    } = this.props;
    const { email, token } = computedMatch.params;
    const newUser = window.location.search.match('new');

    if (!newUser) {
      this.setState({ acceptTerms: true });
    }

    connectedAccountClearError();
    connectedSessionClearError();
    connectedSetCredentialData('email', decodeURIComponent(email));
    connectedVerifyVerificationToken(token);
  }

  // This lifecycle method is used to perform a side effect.
  // When the password form is submitted, it will send a request,
  // and setPasswordRequestOut === true. Once the request is
  // complete, setPasswordRequestOut === false. If there isn't an
  // error, the side effect of sending a signin request occurs.
  componentDidUpdate(prevProps) {
    const { setPasswordRequestOut: prevSetPasswordRequestOut } = prevProps;
    const { connectedSendSignInRequest, setPasswordRequestOut, accountError } = this.props;
    const { password } = this.state;

    if (prevSetPasswordRequestOut && !setPasswordRequestOut && !accountError) {
      connectedSendSignInRequest({ password });
    }
  }

  checkPasswordLength = () => {
    const { password, passwordLengthError } = this.state;
    const showErrorLength = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
    if (showErrorLength !== passwordLengthError) {
      this.setState({ passwordLengthError: showErrorLength });
    }
  }

  handleSubmit = (e, acceptTerms) => {
    if (acceptTerms) {
      this.setPassword(e);
    }
  }

  passwordOnChange = (e) => {
    this.setState({ password: e.target.value }, this.debouncedCheckPasswordLength);
  }

  setPassword = (e) => {
    const {
      computedMatch, connectedSendSetPassword,
    } = this.props;
    const { password } = this.state;
    e.preventDefault();
    connectedSendSetPassword(password, computedMatch.params.token);
    this.setState({
      submitted: true,
    });
  }

  toggleCheckboxState = (key) => {
    const newstate = { ...this.state };
    newstate[key] = !newstate[key];
    this.setState(newstate);
  }

  renderPPandToS = (acceptTerms) => (
    <>
      <Checkbox
        checked={acceptTerms}
        label="I accept"
        onChange={() => this.toggleCheckboxState('acceptTerms')}
        value={acceptTerms}
      />
      <span>&nbsp;the</span>
      <a href="https://remeeting.com/privacy" target="_blank" rel="noreferrer"> Privacy Policy </a>
      <span>and</span>
      <a href="https://remeeting.com/terms" target="_blank" rel="noreferrer"> Terms of Service</a>
      <span>.</span>
    </>
  )

  renderSetPasswordForm = () => {
    const {
      accountError,
      sessionEmail,
      sessionError,
    } = this.props;
    const { acceptTerms, password } = this.state;
    const newUser = window.location.search.match('new');
    const error = sessionError || accountError;
    return (
      <div>
        <Header color={error && 'grey'}>
          {`Set a password for ${sessionEmail}.`}
        </Header>
        <p style={error && { color: '#dddddd' }}>
          This will also sign in to your account.
        </p>
        <Form onSubmit={(e) => this.handleSubmit(e, acceptTerms)} key="form">
          <Form.Input
            type="password"
            placeholder={`Password (at least ${PASSWORD_MIN_LENGTH} characters)`}
            value={password}
            onChange={this.passwordOnChange}
            required
          />
          { newUser && this.renderPPandToS(acceptTerms) }
          { newUser && <Divider /> }
          { this.renderSubmitArea() }
        </Form>
      </div>
    );
  }

  renderSubmitArea = () => {
    const { submitted, acceptTerms, password } = this.state;
    const {
      setPasswordRequestOut, signInRequestOut, verifyTokenError,
    } = this.props;

    return (
      <div className="re is-flex is-flex-row is-flex-align-center is-flex-justify-space-between">
        { submitted && (setPasswordRequestOut || signInRequestOut)
          ? <Loading />
          : (
            <Button
              type="submit"
              disabled={verifyTokenError || !acceptTerms || password.length < PASSWORD_MIN_LENGTH}
              primary
            >
              Set password
            </Button>
          )}
        {/* <Checkbox */}
        {/*   checked={rememberMe} */}
        {/*   label="Remember me for a week" */}
        {/*   onChange={() => this.toggleCheckboxState('rememberMe')} */}
        {/*   value={rememberMe} */}
        {/* /> */}
      </div>
    );
  }

  renderErrorArea = () => {
    const { accountError, sessionError, verifyTokenError } = this.props;
    const { passwordLengthError } = this.state;
    let msg = '';

    if (accountError) {
      msg = (accountError.error && accountError.error.message)
        || 'Sorry, we\'re unable to set the new password.';
    } else if (sessionError) {
      msg = 'Sorry, we\'re unable to sign in using the new password.';
    } else if (verifyTokenError) {
      msg = 'Your password cannot be set because this verification link expired, or the link was invalidated by a more recent email sent to verify your account.';
    } else if (passwordLengthError) {
      msg = `Your password must be at least ${PASSWORD_MIN_LENGTH} characters long.`;
    } else {
      return <div />;
    }

    let btn;
    if (verifyTokenError) {
      btn = <EmailNewSetPassword />;
    }

    return (
      <>
        <ErrorMessage message={msg} />
        <div className="re is-marginTop-16px">
          { btn }
        </div>
      </>
    );
  }

  render() {
    const {
      verifyTokenError,
      verifyTokenRequestOut,
      routeName,
    } = this.props;
    let renderedComponent;
    if (verifyTokenRequestOut) {
      renderedComponent = <Loading centered />;
    } else if (verifyTokenError) {
      renderedComponent = this.renderErrorArea();
    } else {
      renderedComponent = (
        <>
          { this.renderSetPasswordForm() }
          { this.renderErrorArea() }
        </>
      );
    }
    return (
      <CenterCard routeName={routeName}>
        { renderedComponent }
      </CenterCard>
    );
  }
}

Verify.propTypes = {
  computedMatch: PropTypes.shape({
    params: PropTypes.shape({
      email: PropTypes.string,
      token: PropTypes.string,
    }),
  }).isRequired,
  connectedAccountClearError: PropTypes.func.isRequired,
  connectedSessionClearError: PropTypes.func.isRequired,
  connectedSetCredentialData: PropTypes.func.isRequired,
  connectedVerifyVerificationToken: PropTypes.func.isRequired,
  connectedSendSignInRequest: PropTypes.func.isRequired,
  setPasswordRequestOut: PropTypes.bool.isRequired,
  accountError: ErrorType,
  connectedSendSetPassword: PropTypes.func.isRequired,
  sessionEmail: PropTypes.string.isRequired,
  sessionError: ErrorType,
  verifyTokenError: PropTypes.bool.isRequired,
  verifyTokenRequestOut: PropTypes.bool.isRequired,
  signInRequestOut: PropTypes.bool.isRequired,
  routeName: PropTypes.string.isRequired,
};

Verify.defaultProps = {
  accountError: undefined,
  sessionError: undefined,
};

const mapStateToProps = (state) => ({
  sessionEmail: state.session.email,
  setPasswordRequestOut: state.account.setPasswordRequestOut,
  accountError: state.account.error,
  signInRequestOut: state.session.signInRequestOut,
  sessionError: state.session.error,
  signedIn: state.session.signedIn,
  verifyTokenError: state.account.verifyTokenFail,
  verifyTokenRequestOut: state.account.verifyTokenRequestOut,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  connectedAccountClearError: accountClearError,
  connectedSessionClearError: sessionClearError,
  connectedSendSetPassword: sendSetPassword,
  connectedSendSignInRequest: sendSignInRequest,
  connectedSetCredentialData: setCredentialData,
  connectedVerifyVerificationToken: verifyVerificationToken,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Verify);
